import React, { useState, useEffect, useMemo } from 'react';

function experienceMatchesSearchTerm(experience, searchTerm) {
  const term = searchTerm.toLowerCase();
  const fields = [
    experience.title,
    experience.snippet,
    experience.the_situation,
    experience.the_solution,
    experience.the_outcome,
  ].filter(Boolean);
  return fields.some((field) =>
    String(field).toLowerCase().includes(term)
  );
}

function experienceMatchesSelectedIndustry(experience, selectedIndustry) {
  if (selectedIndustry === 'all') return true;
  return experience.industries && experience.industries.includes(selectedIndustry);
}

function getIndustries(experience, industries) {
  if (!experience.industries || experience.industries.length === 0) return '';
  return experience.industries
    .map((industryKey) => {
      const industry = industries.find((i) => i.slug === industryKey);
      return industry ? industry.title : industryKey;
    })
    .join(', ');
}

export default function ExperienceListing() {
  const [experiences, setExperiences] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch('/!/experiences/entries').then((r) => r.json()),
      fetch('/!/experiences/industries').then((r) => r.json()),
    ])
      .then(([experiencesData, industriesData]) => {
        setExperiences(experiencesData);
        setIndustries(industriesData);
      })
      .catch((err) => {
        setError(err.message);
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredExperiences = useMemo(() => {
    return experiences.filter(
      (exp) =>
        experienceMatchesSearchTerm(exp, searchTerm) &&
        experienceMatchesSelectedIndustry(exp, selectedIndustry)
    );
  }, [experiences, searchTerm, selectedIndustry]);

  if (loading) {
    return (
      <div className="container wrapper content">
        <p>Loading experiences...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container wrapper content">
        <p>Failed to load experiences. Please try again later.</p>
      </div>
    );
  }

  return (
    <>
      <div className="container wrapper">
        <div className="page-title">
          <h1>Experience</h1>
          <div className="experience-filter-tools">
            <div className="search-input">
              <input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-dropdown">
              <select
                value={selectedIndustry}
                onChange={(e) => setSelectedIndustry(e.target.value)}
              >
                {industries.map((industry) => (
                  <option key={industry.slug} value={industry.slug}>
                    {industry.title}
                  </option>
                ))}
              </select>
              <div className="filter-arrow">
                <i className="far fa-arrow-down" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container wrapper content flex experience-grid">
        {filteredExperiences.map((experience) => (
          <article
            key={experience.slug}
            className="hover-item my-8 flex flex-col"
          >
            <a href={experience.url}>
              <img
                className="experience-image"
                src={experience.hero_image}
                width={900}
                height={500}
                alt={experience.title}
              />
            </a>
            <div className="white-page-with-image h-full flex flex-col">
              <div>
                <h2>
                  <a href={experience.url}>{experience.title}</a>
                </h2>
                <p>{getIndustries(experience, industries)}</p>
                <div dangerouslySetInnerHTML={{ __html: experience.snippet }} />
              </div>
              <div className="mt-auto">
                <a className="btn" href={experience.url}>
                  Read more
                </a>
              </div>
            </div>
          </article>
        ))}
      </div>
    </>
  );
}
